import { useEffect } from 'react';
import { create } from 'zustand';
import { agenciesApi, type AgencyMembership } from '../api/agencies.api';
import { useAuthStore } from './auth.store';
import { useActingAgency } from './actingAgency.store';

export const APP_MODES = ['personal', 'agency', 'admin'] as const;
export type AppMode = typeof APP_MODES[number];

const modeKey = (userId: string) => `gromly.mode.${userId}`;

/** The last mode this user chose on this device, if any. */
function readMode(userId: string): AppMode | null {
  try {
    const stored = localStorage.getItem(modeKey(userId));
    return APP_MODES.find(m => m === stored) ?? null;
  } catch {
    return null;
  }
}

interface AgenciesState {
  /** Whose list was asked for: a different user signing in asks again. */
  requestedFor: string | null;
  /** Whose list `agencies` is. Until it matches the user, the answer is unknown. */
  loadedFor: string | null;
  agencies: AgencyMembership[];
  /** The mode chosen on this device; null until one is. */
  mode: AppMode | null;
  load: (userId: string) => void;
  /** After joining an agency: ask again, and land in its mode. */
  joined: (userId: string) => void;
  setMode: (userId: string, mode: AppMode) => void;
}

export const useAgenciesStore = create<AgenciesState>((set, get) => ({
  requestedFor: null,
  loadedFor: null,
  agencies: [],
  mode: null,
  load: (userId) => {
    if (get().requestedFor === userId) return;
    set({ requestedFor: userId, mode: readMode(userId) });
    agenciesApi.mine()
      // The server holds the boundary; a list that can't be fetched just means the traveller's screens.
      .catch(() => [] as AgencyMembership[])
      .then(agencies => {
        if (get().requestedFor === userId) set({ agencies, loadedFor: userId });
      });
  },
  joined: (userId) => {
    get().setMode(userId, 'agency');
    set({ requestedFor: null, loadedFor: null });
    get().load(userId);
  },
  setMode: (userId, mode) => {
    // Switching experience from the menu is leaving an agency entered as Gromly (ADR-0041).
    useActingAgency.getState().leave();
    try { localStorage.setItem(modeKey(userId), mode); } catch { /* the choice just won't outlive the tab */ }
    set({ mode });
  },
}));

export type MyAgencies =
  | { status: 'loading' }
  | {
    status: 'ready';
    agencies: AgencyMembership[];
    /** The experience showing: one of `modes`. */
    mode: AppMode;
    /** The experiences this account has; the switch shows only with two or more. */
    modes: AppMode[];
    /** The agency whose screens are showing — set only in agency mode. */
    agency: AgencyMembership | null;
    isStaff: boolean;
    setMode: (mode: AppMode) => void;
  };

/**
 * ADR-0036: an account can be a traveller, agency staff and a Gromly admin.
 * It switches between the experiences it has; the others never show. The mode
 * is the UI's only — the server decides per trip, and `requireAdmin` per call.
 * A first visit is the agency's for staff (ADR-0036), and personal otherwise:
 * admins travel too.
 */
export function useMyAgencies(): MyAgencies {
  const userId = useAuthStore(s => s.user?.id ?? null);
  const isAdmin = useAuthStore(s => s.user?.isAdmin ?? false);
  const { loadedFor, agencies: own, mode: storedMode, load, setMode } = useAgenciesStore();
  const acting = useActingAgency(st => st.agency);

  useEffect(() => { if (userId) load(userId); }, [userId, load]);

  if (!userId || loadedFor !== userId) return { status: 'loading' };
  // ADR-0041: an admin inside an agency is in it as its admin, for as long as they stay.
  const inside = isAdmin && acting ? { ...acting, role: 'ADMIN' as const } : null;
  const agencies = inside ? [inside, ...own.filter(a => a.id !== inside.id)] : own;
  const chosen = inside ? 'agency' : storedMode;
  const isStaff = agencies.length > 0;
  const modes = APP_MODES.filter(m => m === 'personal' || (m === 'agency' && isStaff) || (m === 'admin' && isAdmin));
  const mode = chosen && modes.includes(chosen) ? chosen : isStaff ? 'agency' : 'personal';
  return {
    status: 'ready',
    agencies,
    mode,
    modes,
    agency: mode === 'agency' ? agencies[0] : null,
    isStaff,
    setMode: m => setMode(userId, m),
  };
}
